/**
 * AdminAgedLeads.jsx — Admin view of the Aged Leads Pipeline
 *
 * Tabs:
 *  1. Pool      — unassigned leads waiting for assignment
 *  2. Assigned  — leads currently with an agent (countdown, round)
 *  3. Cold      — leads in 21-day cooling period
 *  4. Closed-Out — terminal exits
 *  5. Settings  — enable/disable, thresholds
 *  6. Agents    — enrollment toggles, capacity, states
 *  7. Reports   — analytics
 *
 * Manual override modal: assign specific lead → specific agent
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from '../lib/api.js';
import TopBar from '../components/TopBar.jsx';
import toast from 'react-hot-toast';
import {
  Settings, Users, BarChart2, Snowflake, CheckCircle2,
  UserCheck, Clock, RefreshCw, Play, XCircle, ChevronDown,
  Loader2, AlertTriangle,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────

function StatusChip({ status }) {
  const map = {
    pool:        { color: 'bg-blue-500/20 text-blue-300 border-blue-500/30',    label: 'Pool' },
    assigned:    { color: 'bg-civix-purple/20 text-purple-300 border-civix-purple/30', label: 'Assigned' },
    cold:        { color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/30',    label: 'Cold' },
    'closed-out':{ color: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30', label: 'Closed Out' },
  };
  const s = map[status] || { color: 'bg-white/10 text-white/50 border-white/10', label: status };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${s.color}`}>{s.label}</span>
  );
}

function Table({ leads, rotationDays = 7, onManualAssign }) {
  if (!leads?.length) return <div className="text-white/40 text-center py-10">No leads in this category.</div>;
  const now = Date.now();
  return (
    <div className="overflow-x-auto rounded-lg border border-civix-border/60">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-civix-border/60 text-white/40 text-xs uppercase tracking-wider">
            <th className="text-left px-4 py-2.5">Name</th>
            <th className="text-left px-4 py-2.5">State</th>
            <th className="text-left px-4 py-2.5">Status</th>
            <th className="text-left px-4 py-2.5">Aged Status</th>
            <th className="text-left px-4 py-2.5">Round</th>
            <th className="text-left px-4 py-2.5">Agent</th>
            <th className="text-left px-4 py-2.5">Time Left / Cold Until</th>
            <th className="text-left px-4 py-2.5">Actions</th>
          </tr>
        </thead>
        <tbody>
          {leads.map((l) => {
            let timeLabel = '—';
            if (l.agedStatus === 'assigned' && l.aged?.assignedAt) {
              const exp = new Date(l.aged.assignedAt).getTime() + rotationDays * 86400000;
              const d   = Math.ceil(Math.max(0, exp - now) / 86400000);
              timeLabel = `${d}d left`;
            } else if (l.agedStatus === 'cold' && l.aged?.coldUntil) {
              timeLabel = new Date(l.aged.coldUntil).toLocaleDateString();
            }
            return (
              <tr key={l._id} className="border-b border-civix-border/30 hover:bg-white/2 transition">
                <td className="px-4 py-2.5 font-medium">{l.firstName} {l.lastName}</td>
                <td className="px-4 py-2.5 text-white/60">{l.state || '—'}</td>
                <td className="px-4 py-2.5 text-white/60 text-xs">{l.status}</td>
                <td className="px-4 py-2.5"><StatusChip status={l.agedStatus} /></td>
                <td className="px-4 py-2.5 text-white/60">{l.aged?.assignmentRound ?? 0}</td>
                <td className="px-4 py-2.5 text-white/60">
                  {l.aged?.currentAgent?.name || l.aged?.currentAgent || '—'}
                </td>
                <td className="px-4 py-2.5 text-white/60 text-xs">{timeLabel}</td>
                <td className="px-4 py-2.5">
                  {onManualAssign && (
                    <button
                      onClick={() => onManualAssign(l)}
                      className="text-xs btn-ghost py-1 px-2"
                    >Assign</button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Manual assign modal
// ─────────────────────────────────────────────────────────────────

function ManualAssignModal({ lead, onClose, onSuccess }) {
  const [agentId, setAgentId] = useState('');
  const [loading, setLoading] = useState(false);

  const { data: agentsData } = useQuery({
    queryKey: ['agents-list'],
    queryFn:  () => apiGet('/agents'),
  });
  const agents = agentsData?.agents || [];

  async function submit() {
    if (!agentId) { toast.error('Select an agent'); return; }
    setLoading(true);
    try {
      await apiPost('/aged-leads/admin/assign-manual', { leadId: lead._id, agentId });
      toast.success('Lead assigned');
      onSuccess();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Assignment failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs">
      <div className="panel p-6 w-full max-w-md space-y-4">
        <div className="font-display font-bold text-lg">Manual Assign</div>
        <p className="text-sm text-white/60">
          Assign <strong>{lead.firstName} {lead.lastName}</strong> ({lead.state}) to an agent.
        </p>
        <select
          value={agentId}
          onChange={(e) => setAgentId(e.target.value)}
          className="input w-full"
        >
          <option value="">Select agent…</option>
          {agents.map((a) => (
            <option key={a._id} value={a._id}>
              {a.name} — {(a.licensedStates || []).join(', ') || 'no states'}
              {a.agedLeadsEnrolled ? ' ✓' : ' (not enrolled)'}
            </option>
          ))}
        </select>
        <div className="flex gap-3 pt-2">
          <button onClick={onClose} className="btn-ghost flex-1">Cancel</button>
          <button onClick={submit} disabled={loading} className="btn-primary flex-1 flex items-center justify-center gap-2">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Assign
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Tab: Settings
// ─────────────────────────────────────────────────────────────────

function SettingsTab() {
  const qc = useQueryClient();
  const { data: tenantData } = useQuery({
    queryKey: ['tenant-config'],
    queryFn:  () => apiGet('/tenant'),
  });

  const cfg = tenantData?.tenant?.agedLeads || {};
  const [enabled,          setEnabled]          = useState(cfg.enabled ?? false);
  const [ageThresholdDays, setAgeThresholdDays] = useState(cfg.ageThresholdDays ?? 31);
  const [rotationDays,     setRotationDays]     = useState(cfg.rotationDays ?? 7);
  const [coldDays,         setColdDays]         = useState(cfg.coldDays ?? 21);
  const [termStatuses,     setTermStatuses]     = useState((cfg.terminalStatuses || []).join(', '));
  const [saving, setSaving] = useState(false);

  async function save() {
    setSaving(true);
    try {
      await apiPost('/aged-leads/admin/config', {
        enabled,
        ageThresholdDays: Number(ageThresholdDays),
        rotationDays:     Number(rotationDays),
        coldDays:         Number(coldDays),
        terminalStatuses: termStatuses.split(',').map((s) => s.trim().toUpperCase()).filter(Boolean),
      });
      qc.invalidateQueries({ queryKey: ['tenant-config'] });
      toast.success('Settings saved');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function runPromote() {
    try {
      const r = await apiPost('/aged-leads/admin/run-promote', {});
      toast.success(`Promoted ${r.promoted}, assigned ${r.assigned}`);
    } catch { toast.error('Failed'); }
  }

  async function runRotate() {
    try {
      const r = await apiPost('/aged-leads/admin/run-rotate', {});
      toast.success(`Rotated ${r.rotated}, revived ${r.revived}, assigned ${r.assigned}`);
    } catch { toast.error('Failed'); }
  }

  return (
    <div className="space-y-5 max-w-lg">
      {/* Enable toggle */}
      <div className="panel p-4 flex items-center justify-between">
        <div>
          <div className="font-semibold">Enable Aged Leads Pipeline</div>
          <div className="text-xs text-white/50 mt-0.5">When disabled, all cron jobs no-op for this tenant.</div>
        </div>
        <button
          onClick={() => setEnabled((v) => !v)}
          className={`relative w-12 h-6 rounded-full transition-colors ${enabled ? 'bg-civix-purple' : 'bg-white/20'}`}
        >
          <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${enabled ? 'left-7' : 'left-1'}`} />
        </button>
      </div>

      {/* Thresholds */}
      <div className="panel p-4 space-y-4">
        <div className="font-semibold mb-1">Pipeline Thresholds</div>

        {[
          { label: 'Age Threshold (days)', desc: 'Days since last touch before lead enters pool', value: ageThresholdDays, set: setAgeThresholdDays },
          { label: 'Rotation Days',        desc: 'Days each agent has before lead rotates',       value: rotationDays,     set: setRotationDays },
          { label: 'Cold Days',            desc: 'Days in cold before lead re-enters pool',       value: coldDays,         set: setColdDays },
        ].map(({ label, desc, value, set }) => (
          <div key={label}>
            <label className="text-xs text-white/50 uppercase tracking-wider">{label}</label>
            <div className="text-[11px] text-white/35 mb-1">{desc}</div>
            <input
              type="number"
              min={1}
              value={value}
              onChange={(e) => set(e.target.value)}
              className="input w-28"
            />
          </div>
        ))}

        <div>
          <label className="text-xs text-white/50 uppercase tracking-wider">Terminal Statuses</label>
          <div className="text-[11px] text-white/35 mb-1">Comma-separated. Lead exits cycle when it hits one of these statuses.</div>
          <input
            type="text"
            value={termStatuses}
            onChange={(e) => setTermStatuses(e.target.value)}
            className="input w-full"
          />
        </div>
      </div>

      <div className="flex gap-3">
        <button onClick={save} disabled={saving} className="btn-primary flex items-center gap-2">
          {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save Settings
        </button>
      </div>

      {/* Manual triggers */}
      <div className="panel p-4 space-y-3">
        <div className="font-semibold text-sm">Manual Triggers (testing)</div>
        <div className="flex gap-3 flex-wrap">
          <button onClick={runPromote} className="btn-ghost flex items-center gap-1 text-sm">
            <Play className="w-3.5 h-3.5" /> Run Promote
          </button>
          <button onClick={runRotate} className="btn-ghost flex items-center gap-1 text-sm">
            <RefreshCw className="w-3.5 h-3.5" /> Run Rotate / Revive
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Tab: Agents
// ─────────────────────────────────────────────────────────────────

function AgentsTab() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['agents-aged-leads'],
    queryFn:  () => apiGet('/agents'),
  });
  const agents = data?.agents || [];

  async function toggleEnroll(agent) {
    try {
      await apiPost('/aged-leads/admin/enroll', {
        agentId: agent._id,
        enrolled: !agent.agedLeadsEnrolled,
      });
      qc.invalidateQueries({ queryKey: ['agents-aged-leads'] });
      toast.success(agent.agedLeadsEnrolled ? 'Unenrolled' : 'Enrolled');
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed');
    }
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-civix-border/60">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-civix-border/60 text-white/40 text-xs uppercase tracking-wider">
            <th className="text-left px-4 py-2.5">Agent</th>
            <th className="text-left px-4 py-2.5">Licensed States</th>
            <th className="text-left px-4 py-2.5">Enrolled</th>
            <th className="text-left px-4 py-2.5">Capacity</th>
          </tr>
        </thead>
        <tbody>
          {agents.map((a) => (
            <tr key={a._id} className="border-b border-civix-border/30 hover:bg-white/2 transition">
              <td className="px-4 py-3">
                <div className="font-medium">{a.name}</div>
                <div className="text-xs text-white/40">{a.email}</div>
              </td>
              <td className="px-4 py-3 text-white/60 text-xs">
                {(a.licensedStates || []).length > 0
                  ? (a.licensedStates || []).join(', ')
                  : <span className="text-amber-400">No states</span>}
              </td>
              <td className="px-4 py-3">
                <button
                  onClick={() => toggleEnroll(a)}
                  className={`relative w-10 h-5 rounded-full transition-colors ${a.agedLeadsEnrolled ? 'bg-civix-purple' : 'bg-white/20'}`}
                >
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${a.agedLeadsEnrolled ? 'left-5' : 'left-0.5'}`} />
                </button>
              </td>
              <td className="px-4 py-3 text-white/60">{a.agedLeadsCapacity ?? 50}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Tab: Reports
// ─────────────────────────────────────────────────────────────────

function ReportsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['aged-leads-reports'],
    queryFn:  () => apiGet('/aged-leads/admin/reports'),
    refetchInterval: 300000,
  });

  if (isLoading) return <div className="text-white/40 py-10 text-center">Loading reports…</div>;

  const t = data?.totals || {};
  const perAgent = data?.perAgent || [];

  return (
    <div className="space-y-6">
      {/* Summary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Aged',         value: t.total        || 0, color: 'text-white' },
          { label: 'Conversion Rate',    value: `${t.conversionRate || 0}%`, color: 'text-emerald-400' },
          { label: 'Avg Rounds',         value: t.avgRounds    || 0, color: 'text-purple-400' },
          { label: 'Expiry Waste %',     value: `${t.expiryWastePct || 0}%`, color: 'text-amber-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="panel p-4">
            <div className="text-xs text-white/40 uppercase tracking-wider mb-1">{label}</div>
            <div className={`font-display font-bold text-2xl ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {/* Status breakdown */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Pool',       value: t.pool      || 0 },
          { label: 'Assigned',   value: t.assigned  || 0 },
          { label: 'Cold',       value: t.cold      || 0 },
          { label: 'Closed Out', value: t.closedOut || 0 },
        ].map(({ label, value }) => (
          <div key={label} className="panel p-4">
            <div className="text-xs text-white/40 mb-1">{label}</div>
            <div className="font-bold text-xl">{value}</div>
          </div>
        ))}
      </div>

      {/* Per-agent leaderboard */}
      <div>
        <div className="font-display font-bold text-base mb-3">Agent Leaderboard</div>
        <div className="overflow-x-auto rounded-lg border border-civix-border/60">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-civix-border/60 text-white/40 text-xs uppercase tracking-wider">
                <th className="text-left px-4 py-2.5">Agent</th>
                <th className="text-left px-4 py-2.5">Handled</th>
                <th className="text-left px-4 py-2.5">Closed</th>
                <th className="text-left px-4 py-2.5">Close Rate</th>
              </tr>
            </thead>
            <tbody>
              {perAgent.map((a) => (
                <tr key={a.agentId} className="border-b border-civix-border/30 hover:bg-white/2">
                  <td className="px-4 py-3 font-medium">{a.agentName || 'Unknown'}</td>
                  <td className="px-4 py-3 text-white/60">{a.handled}</td>
                  <td className="px-4 py-3 text-white/60">{a.closed}</td>
                  <td className="px-4 py-3">
                    <span className="text-emerald-400 font-semibold">
                      {Math.round((a.closeRate || 0) * 100)}%
                    </span>
                  </td>
                </tr>
              ))}
              {!perAgent.length && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-white/30">No data yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// Main admin component
// ─────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'pool',       label: 'Pool',       icon: RefreshCw },
  { key: 'assigned',   label: 'Assigned',   icon: UserCheck },
  { key: 'cold',       label: 'Cold',       icon: Snowflake },
  { key: 'closed-out', label: 'Closed Out', icon: CheckCircle2 },
  { key: 'settings',   label: 'Settings',   icon: Settings },
  { key: 'agents',     label: 'Agents',     icon: Users },
  { key: 'reports',    label: 'Reports',    icon: BarChart2 },
];

export default function AdminAgedLeads() {
  const [activeTab, setActiveTab] = useState('pool');
  const [manualLead, setManualLead] = useState(null);
  const qc = useQueryClient();

  const { data: tenantData } = useQuery({
    queryKey: ['tenant-config'],
    queryFn:  () => apiGet('/tenant'),
  });
  const rotationDays = tenantData?.tenant?.agedLeads?.rotationDays ?? 7;

  const leadsQuery = useQuery({
    queryKey: ['aged-leads-admin', activeTab],
    queryFn: () => {
      if (['settings', 'agents', 'reports'].includes(activeTab)) return null;
      if (activeTab === 'pool')
        return apiGet('/aged-leads/admin/pool', { limit: 100 });
      return apiGet('/aged-leads/admin/all', { agedStatus: activeTab, limit: 100 });
    },
    enabled: !['settings', 'agents', 'reports'].includes(activeTab),
    refetchInterval: 60000,
  });

  const leads = leadsQuery.data?.leads || [];

  return (
    <div className="space-y-4">
      <TopBar title="Admin: Lead Pool" subtitle="Aged Leads Pipeline Management" />

      {/* Tab bar */}
      <div className="flex items-center gap-1 p-1 rounded-lg bg-civix-panel border border-civix-border/60 flex-wrap">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition ${
                activeTab === t.key
                  ? 'bg-civix-purple text-white shadow-sm'
                  : 'text-white/50 hover:text-white'
              }`}
            >
              <Icon className="w-3.5 h-3.5" />
              {t.label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {activeTab === 'settings' && <SettingsTab />}
      {activeTab === 'agents'   && <AgentsTab />}
      {activeTab === 'reports'  && <ReportsTab />}

      {!['settings', 'agents', 'reports'].includes(activeTab) && (
        leadsQuery.isLoading
          ? <div className="text-white/40 text-center py-10">Loading…</div>
          : <Table
              leads={leads}
              rotationDays={rotationDays}
              onManualAssign={setManualLead}
            />
      )}

      {/* Manual assign modal */}
      {manualLead && (
        <ManualAssignModal
          lead={manualLead}
          onClose={() => setManualLead(null)}
          onSuccess={() => qc.invalidateQueries({ queryKey: ['aged-leads-admin'] })}
        />
      )}
    </div>
  );
}
